import { z } from "zod";
import type { Direction, Horizon } from "./astro";

/**
 * A declarative description of how a time is calculated. Every halachic opinion is
 * expressed as a tree of anchors, so new opinions are data, not code.
 */
export type Anchor =
  | { kind: "sunrise"; horizon: Horizon }
  | { kind: "sunset"; horizon: Horizon }
  | { kind: "depression"; degrees: number; dir: Direction }
  | { kind: "transit" }
  | {
      kind: "offset";
      from: Anchor;
      minutes: number;
      unit: "fixed" | "zmaniyot";
      /** The day that defines a temporal minute. Defaults to sea-level sunrise to sunset. */
      zmaniyotDay?: DayDef;
    }
  | { kind: "hours"; day: DayDef; hours: number }
  | { kind: "midpoint"; a: Anchor; b: Anchor }
  | { kind: "nextDay"; anchor: Anchor }
  | { kind: "latest"; of: Anchor[] }
  | { kind: "earliest"; of: Anchor[] };

export interface DayDef {
  start: Anchor;
  end: Anchor;
}

const horizonSchema = z.enum(["sea_level", "elevation"]);
const directionSchema = z.enum(["morning", "evening"]);

export const dayDefSchema: z.ZodType<DayDef> = z.lazy(() =>
  z.strictObject({ start: anchorSchema, end: anchorSchema }),
);

export const anchorSchema: z.ZodType<Anchor> = z.lazy(() =>
  z.union([
    z.strictObject({ kind: z.literal("sunrise"), horizon: horizonSchema }),
    z.strictObject({ kind: z.literal("sunset"), horizon: horizonSchema }),
    z.strictObject({
      kind: z.literal("depression"),
      degrees: z.number().min(0).max(30),
      dir: directionSchema,
    }),
    z.strictObject({ kind: z.literal("transit") }),
    z.strictObject({
      kind: z.literal("offset"),
      from: anchorSchema,
      minutes: z.number(),
      unit: z.enum(["fixed", "zmaniyot"]),
      zmaniyotDay: dayDefSchema.optional(),
    }),
    z.strictObject({
      kind: z.literal("hours"),
      day: dayDefSchema,
      hours: z.number().min(0).max(24),
    }),
    z.strictObject({ kind: z.literal("midpoint"), a: anchorSchema, b: anchorSchema }),
    z.strictObject({ kind: z.literal("nextDay"), anchor: anchorSchema }),
    z.strictObject({ kind: z.literal("latest"), of: z.array(anchorSchema).min(1) }),
    z.strictObject({ kind: z.literal("earliest"), of: z.array(anchorSchema).min(1) }),
  ]),
);

export const ZMAN_IDS = [
  "alos",
  "misheyakir",
  "sunrise",
  "sof_zman_shma",
  "sof_zman_tfila",
  "chatzot",
  "mincha_gedola",
  "mincha_ketana",
  "plag_hamincha",
  "sunset",
  "tzeit",
  "tzeit_rt",
  "chatzot_night",
  "sof_zman_achilat_chametz",
  "sof_zman_biur_chametz",
] as const;

export type ZmanId = (typeof ZMAN_IDS)[number];

/** "floor" for deadlines (must not be late), "ceil" for earliest-allowed times. */
export type Rounding = "floor" | "ceil";

const localizedSchema = z.strictObject({ he: z.string().min(1), en: z.string().min(1) });

export const opinionSchema = z.strictObject({
  id: z.string().regex(/^[a-z_]+\.[a-z0-9_]+$/),
  zman: z.enum(ZMAN_IDS),
  label: localizedSchema,
  calc: anchorSchema,
  rounding: z.enum(["floor", "ceil"]),
  explanation: localizedSchema,
  sources: z.array(z.string()).optional(),
  verified: z.strictObject({ by: z.string(), date: z.string(), against: z.string() }).optional(),
});

export type Opinion = z.infer<typeof opinionSchema>;

export const profileSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]+$/),
  label: localizedSchema,
  description: localizedSchema,
  /** draft: not yet verified; partial: some opinions need features not built yet. */
  status: z.enum(["draft", "partial", "verified"]),
  notes: localizedSchema.optional(),
  /** What a rav has to settle before this profile can be marked verified (SPEC 10.3). */
  openQuestions: z.array(localizedSchema).optional(),
  opinions: z.record(z.enum(ZMAN_IDS), z.string()),
});

export type Profile = z.infer<typeof profileSchema>;
